<script>
import { defineComponent } from "vue";
import SmsPatternPicker from "@/view/components/pickers/smsPatternPicker.vue";
import PatternInputsGenerate from "@/view/components/fragments/patternInputsGenerate.vue";
import CustomDatePicker from "@/view/pages/list/components/cardList/custom-date-picker.vue";
import { mapGetters } from "vuex";
import { getBankInfoByCardNumber } from "../js/getBankNameByBankNumber";
import ButtonTempl from "@/view/pages/uiBeautifier/buttonTempl.vue";
import ArchivedCardsDialog from "./ArchivedAccount.vue";

export default defineComponent({
  name: "ManagementAccount",
  components: {
    ButtonTempl,
    CustomDatePicker,
    PatternInputsGenerate,
    SmsPatternPicker,
    ArchivedCardsDialog,
  },

  props: {
    value: {
      type: Object,
      default: () => ({ list: [] }),
    },
  },

  computed: {
    ...mapGetters("localstorage", ["createQrID"]),
    currentQrId() {
      return this.createQrID || 0;
    },

    showSmsPlatform() {
      return this.form.phone && this.form.phone.length === 11;
    },

    smsSwitch: {
      get() {
        return ["sms", "both"].includes(this.form.sms_type);
      },
      set(val) {
        this.updateSmsType(val, this.whatsappSwitch);
      },
    },

    // وضعیت دکمه واتساپ
    whatsappSwitch: {
      get() {
        return ["whatsapp", "both"].includes(this.form.sms_type);
      },
      set(val) {
        this.updateSmsType(this.smsSwitch, val);
      },
    },

    smsDescription() {
      switch (this.form.sms_type) {
        case "sms":
          return "پس از هر واریزی برای مالک حساب پیامک جهت رد و یا تایید واریزی ارسال میشود.";
        case "whatsapp":
          return "پس از هر واریزی برای مالک حساب عکس فیش آن ارسال خواهد شد.";
        case "both":
          return "پس از هر واریزی برای مالک حساب پیامک جهت رد و یا تایید و عکس ویش آن در واتساپ ارسال میشود.";
        default:
          return "";
      }
    },

    phoneRules() {
      return [
        (v) => !v || /^\d+$/.test(v) || "فقط عدد وارد کنید",
        (v) => {
          if (!v) return true;
          return (
            /^(?:0|98)9\d{9}$/.test(v) ||
            "شماره موبایل معتبر نیست (مثلاً ... 0912 یا ... 98912)"
          );
        },
      ];
    },
  },

  data() {
    return {
      localCards: [],
      showAddCard: false,
      itemToDeleteId: null,
      draggingIndex: -1,
      currentStep: 1,
      loading: false,
      pageLoading: false,
      bankLogo: null,
      archivedCardsCount: 0,

      cons: { title: "افزودن کارت" },

      limitPeriodOptions: [
        { text: "بدون محدودیت", value: "unLimited" },
        { text: "کل", value: "total" },
        { text: "ماهانه", value: "monthly" },
        { text: "هفتگی", value: "weekly" },
        { text: "روزانه", value: "daily" },
      ],

      limitSmsTypeOptions: [
        { text: "غیر فعال", value: "disable" },
        { text: "هر دو ( پیامک و واتساپ )", value: "both" },
        { text: "پیامک", value: "sms" },
        { text: "واتساپ", value: "whatsapp" },
      ],

      form: {
        id: null,
        qr_id: 0,
        index: 1,
        description: "",
        bank_name: "",
        account_number: "",
        cart_number: "",
        shaba_number: "",
        owner_name: "",
        phone: "",
        sms_type: "disable",
        limit_mode: null,
        limit_period: null,
        max_amount: "",
        min_amount: "",
        max_tx_count: "",
        max_tx_amount: "",
        transaction_expire: "",
        expire_date: "",
      },

      rules: {
        required: (v) => !!v || "این فیلد الزامی است",
        numeric: (v) => {
          if (!v) return true;
          const clean = v.toString().replace(/,/g, "").replace(/\s/g, "");
          return /^\d+$/.test(clean) || "فقط عدد وارد کنید";
        },
        nameOnly: (v) =>
          /^[آ-یa-zA-Z\s\u200c()]+$/.test(v) || "فقط حروف و پرانتز وارد کنید",
        cardNumber: (v) => {
          if (!v) return "شماره کارت الزامی است";
          let clean = v.toString().replace(/\s/g, "");
          clean = this.english(clean);
          return (
            (clean.length === 16 && /^\d+$/.test(clean)) ||
            "شماره کارت باید ۱۶ رقم باشد"
          );
        },
        sheba: (v) => (v && v.length === 24) || "شماره شبا باید ۲۴ کراکتر باشد",
        mobile: (v) =>
          /^09\d{9}$/.test(v) || "شماره موبایل معتبر نیست (مثلاً ... 0912)",
        minLen: (v) => (v && v.length >= 2) || "حداقل ۲ کاراکتر وارد کنید",
      },
    };
  },

  mounted() {
    console.log("archived = ", this.archivedCardsCount);
    this.form.qr_id = this.currentQrId;
    this.fetchData();
    this.$refs.archiveDialog.fetchData();
  },

  watch: {
    "$i18n.locale": {
      handler() {
        this.$forceUpdate();
      },
      immediate: true,
      deep: true,
    },
    value: {
      handler(newVal) {
        if (
          newVal &&
          newVal.list &&
          Array.isArray(newVal.list) &&
          this.localCards.length === 0
        ) {
          this.localCards = JSON.parse(JSON.stringify(newVal.list));
        }
      },
      immediate: true,
      deep: true,
    },
    "form.limit_mode"(newVal) {
      this.$nextTick(() => {
        const step3Form = this.$refs.step3Form;
        if (step3Form) step3Form.resetValidation();
      });
    },
  },

  methods: {
    // --- Helper Methods ---
    english(value) {
      if (!value) return "";
      return value.toString().replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d));
    },
    validateMaxAmount(v) {
      if (!v) return true;
      const max = parseInt(v.toString().replace(/,/g, ""));
      if (max === 0) return true;
      const minStr = this.form.min_amount;
      if (!minStr) return true;
      const min = parseInt(minStr.toString().replace(/,/g, ""));
      return max >= min || "سقف مبلغ نمی‌تواند کمتر از حداقل مبلغ باشد";
    },

    formattedTomans(value) {
      if (!value) return "";
      let raw = value.toString().replace(/,/g, "");
      return (Number(raw) / 10).toLocaleString() + " تومان";
    },

    handleShebaInput(value) {
      let valueEng = this.english(value);
      if (!valueEng) {
        this.form.shaba_number = "";
        return;
      }
      const clean = valueEng
        .toString()
        .toUpperCase()
        .replace(/IR/g, "")
        .replace(/[^0-9]/g, "");
      this.form.shaba_number = clean.slice(0, 24);
    },

    updateSmsType(isSms, isWhatsapp) {
      if (isSms && isWhatsapp) {
        this.form.sms_type = "both";
      } else if (isSms) {
        this.form.sms_type = "sms";
      } else if (isWhatsapp) {
        this.form.sms_type = "whatsapp";
      } else {
        this.form.sms_type = "disable";
      }
    },

    handleNumericInput(key, value) {
      if (!value) {
        this.form[key] = "";
        return;
      }
      this.form[key] = value.toString().replace(/\D/g, "");
    },

    validateMinAmount(v) {
      if (!v) return true;
      const min = parseInt(v.toString().replace(/,/g, ""));
      const maxStr = this.form.max_amount;
      if (!maxStr) return true;
      const max = parseInt(maxStr.toString().replace(/,/g, ""));
      if (max === 0) return true;
      return min <= max || "حداقل مبلغ نمی‌تواند بیشتر از سقف مبلغ باشد";
    },

    formatCurrency(value) {
      if (!value) return "";
      const rawValue = value.toString().replace(/\D/g, "");
      return rawValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    formatCardNumber(value) {
      if (!value) return "";
      const rawValue = value.toString().replace(/\D/g, "");
      const truncated = rawValue.slice(0, 16);
      return truncated.replace(/(\d{4})/g, "$1 ").trim();
    },

    handleCurrencyInput(key, value) {
      this.form[key] = this.formatCurrency(value);
      if (key === "max_amount" || key === "min_amount") {
        if (this.$refs.step2Form) {
          this.$nextTick(() => {
            this.$refs.step2Form.validate();
          });
        }
      }
    },

    handleCardInput(value) {
      this.form.cart_number = this.formatCardNumber(value);
      const rawValue = value ? value.toString().replace(/\D/g, "") : "";
      if (rawValue.length >= 4) {
        const info = getBankInfoByCardNumber(rawValue);
        if (info) {
          this.form.bank_name = info.name;
          this.bankLogo = info.logo;
        }
      } else {
        this.bankLogo = null;
      }
    },

    nowDateTimeString() {
      return new Date().toISOString().substr(0, 10);
    },

    emitChanges() {
      const updatedObject = { ...this.value, list: this.localCards };
      this.$emit("update:value", updatedObject);
    },

    fetchData() {
      this.pageLoading = true;
      this.$api
        .get("/v1/testQr/smart", {
          params: { qr_id: this.currentQrId },
        })
        .then((res) => {
          const accounts = res.data.data || [];
          if (Array.isArray(accounts)) {
            this.localCards = accounts;
            if (this.localCards.length > 0) {
              const maxIndex = Math.max(
                ...this.localCards.map((c) => c.index || 0)
              );
              this.form.index = maxIndex + 1;
            }
            this.emitChanges();
          }
        })
        .catch((err) => {
          console.error("Error fetching data:", err);
          this.$toast.error("خطا در دریافت لیست کارت‌ها");
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },

    openArchiveDialog() {
      this.$refs.archiveDialog.open();
    },

    onCardRestored() {
      this.fetchData();
    },

    handleSubmit() {
      const step3Form = this.$refs.step3Form;
      if (!step3Form.validate()) {
        this.$toast.warning("اطلاعات مرحله آخر نامعتبر است.");
        return;
      }

      this.loading = true;
      const payload = this.preparePayload();

      if (this.form.id) {
        this.$api
          .put(`/v1/testQr/smart/account/${this.form.id}`, payload)
          .then(() => {
            this.$toast.success("کارت با موفقیت ویرایش شد.");
            this.showAddCard = false;
            this.resetForm();
            this.fetchData();
          })
          .catch(this.handleApiError)
          .finally(() => (this.loading = false));
      } else {
        this.$api
          .post("/v1/testQr/smart/account", payload)
          .then(() => {
            this.$toast.success("کارت با موفقیت ثبت شد.");
            this.form.index++;
            this.showAddCard = false;
            this.resetForm();
            this.fetchData();
          })
          .catch(this.handleApiError)
          .finally(() => (this.loading = false));
      }
    },

    async confirmDelete(id) {
      if (
        await this.$modal.yesNo(
          "انتقال به بایگانی",
          "آیا از حذف کارت اطمبنان داربد؟",
          true
        )
      ) {
        this.itemToDeleteId = id;
        this.deleteCard();
      }
    },

    deleteCard() {
      if (this.itemToDeleteId) {
        this.loading = true;
        this.$api
          .delete(`v1/testQr/smart/account/archive/${this.itemToDeleteId}`, {
            params: { qr_id: this.currentQrId },
          })
          .then(() => {
            this.$toast.success("کارت با موفقیت حذف (بایگانی) شد.");
            this.archivedCardsCount += 1;
            this.itemToDeleteId = null;
            this.fetchData();
          })
          .catch(this.handleApiError)
          .finally(() => {
            this.loading = false;
          });
      }
    },

    nextStep(n) {
      const formRefs = { 1: "step1Form", 2: "step2Form" };
      const refName = formRefs[n];
      const form = this.$refs[refName];

      if (form) {
        if (form.validate()) {
          this.currentStep = n + 1;
        } else {
          this.$toast.warning("لطفا خطاهای موجود را برطرف کنید.");
        }
      }
    },

    prevStep(n) {
      this.currentStep = n - 1;
    },

    onDragStart(index, event) {
      this.draggingIndex = index;
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.dropEffect = "move";
      }
    },
    onDragOver(event) {
      event.preventDefault();
      return false;
    },
    onDrop(toIndex) {
      const fromIndex = this.draggingIndex;
      if (fromIndex === -1 || fromIndex === toIndex) return;
      const itemToMove = this.localCards.splice(fromIndex, 1)[0];
      this.localCards.splice(toIndex, 0, itemToMove);
      this.draggingIndex = -1;
      this.emitChanges();
    },
    onDragEnd() {
      this.draggingIndex = -1;
    },

    openAddMode() {
      this.resetForm();
      this.cons.title = "افزودن کارت جدید";
      this.currentStep = 1;
      this.showAddCard = true;
      this.$nextTick(() => {
        const s1 = this.$refs.step1Form;
        const s2 = this.$refs.step2Form;
        const s3 = this.$refs.step3Form;
        if (s1) s1.resetValidation();
        if (s2) s2.resetValidation();
        if (s3) s3.resetValidation();
      });
    },

    openEditMode(card) {
      this.form = { ...card };

      if (this.form.max_amount)
        this.form.max_amount = this.formatCurrency(this.form.max_amount);
      if (this.form.min_amount)
        this.form.min_amount = this.formatCurrency(this.form.min_amount);
      if (this.form.cart_number) {
        this.form.cart_number = this.formatCardNumber(this.form.cart_number);
        const rawCard = this.form.cart_number.replace(/\D/g, "");
        const info = getBankInfoByCardNumber(rawCard);
        if (info) this.bankLogo = info.logo;
      }
      if (this.form.max_tx_count)
        this.form.max_tx_count = String(this.form.max_tx_count);
      if (this.form.transaction_expire)
        this.form.transaction_expire = String(this.form.transaction_expire);

      if (!this.form.limit_mode) this.form.limit_mode = null;
      if (!this.form.limit_period) this.form.limit_period = "total";
      if (!this.form.sms_type) this.form.sms_type = "disable";

      this.cons.title = "ویرایش کارت";
      this.currentStep = 1;
      this.showAddCard = true;
    },

    resetForm() {
      const nextIndex =
        this.localCards.length > 0
          ? Math.max(...this.localCards.map((c) => c.index || 0)) + 1
          : 1;

      this.form = {
        id: null,
        qr_id: this.currentQrId,
        index: nextIndex,
        description: "",
        bank_name: "",
        account_number: "",
        cart_number: "",
        shaba_number: "",
        owner_name: "",
        phone: "",
        sms_type: "disable",
        limit_mode: null,
        limit_period: null,
        max_amount: "",
        min_amount: "",
        max_tx_count: "",
        max_tx_amount: "",
        transaction_expire: "",
        expire_date: "",
      };
      this.bankLogo = null;
    },

    preparePayload() {
      const payload = {};
      const nullableFields = [
        "max_amount",
        "min_amount",
        "max_tx_count",
        "max_tx_amount",
        "transaction_expire",
        "cart_number",
      ];

      Object.keys(this.form).forEach((key) => {
        let value = this.form[key];
        if (nullableFields.includes(key)) {
          if (typeof value === "string") {
            value = value.replace(/,/g, "").replace(/\s/g, "");
          }
          if (value === "" || value === null || value === undefined) {
            payload[key] = 0;
          } else {
            payload[key] = Number(value);
          }
        } else if (value !== null && value !== undefined && value !== "") {
          payload[key] = value;
        }
      });

      delete payload.id;
      delete payload.limit_period;
      payload.qr_id = this.currentQrId;

      return payload;
    },

    handleApiError({ response }) {
      if (response && response.data && response.data.errors) {
        try {
          const firstKey = Object.keys(response.data.errors)[0];
          const firstError = response.data.errors[firstKey][0];
          this.$toast.error(firstError);
        } catch (e) {
          this.$toast.error("خطا در پردازش اطلاعات.");
        }
      } else {
        this.$toast.error("خطا در برقراری ارتباط با سرور.");
      }
    },
  },
});
</script>

<template>
  <div>
    <v-btn
      v-if="archivedCardsCount > 0"
      class="mb-6 py-4 px-6"
      color="indigo lighten-1"
      dark
      block
      elevation="3"
      rounded
      @click="openArchiveDialog"
    >
      <v-icon left>mdi-archive-arrow-down-outline</v-icon>
      کارت‌های بایگانی شده
    </v-btn>

    <div v-if="pageLoading" class="text-center my-10">
      <v-progress-circular color="primary" indeterminate></v-progress-circular>
      <div class="mt-2 grey--text">در حال دریافت اطلاعات...</div>
    </div>

    <v-row v-else-if="localCards.length > 0" class="mb-4">
      <v-col
        v-for="(card, index) in localCards"
        :key="card.id"
        :class="{ dragging: draggingIndex === index }"
        class="drag-item-wrapper"
        cols="12"
        draggable="true"
        @dragend="onDragEnd"
        @dragover="onDragOver"
        @dragstart="onDragStart(index, $event)"
        @drop="onDrop(index)"
      >
        <v-card class="pa-3 hover-card" outlined>
          <div class="d-flex align-center">
            <div class="d-flex align-center mr-2 ml-md-4 ml-1 grab-handle">
              <v-icon color="grey darken-1" class="d-none d-sm-flex" large
                >mdi-drag-vertical</v-icon
              >
              <v-icon color="grey darken-1" class="d-flex d-sm-none" medium
                >mdi-drag-vertical</v-icon
              >
              <v-chip
                class="mr-1 mr-sm-2 font-weight-bold"
                color="primary"
                label
                small
                >{{ index + 1 }}</v-chip
              >
            </div>

            <div class="flex-grow-1" style="min-width: 0">
              <div class="d-flex justify-space-between align-center">
                <div style="flex: 1; min-width: 0">
                  <div
                    class="font-weight-bold text-h6 text-subtitle-1-mobile mb-1 text-truncate"
                  >
                    {{ card.bank_name }}
                  </div>

                  <div
                    class="grey--text text-body-2 d-flex flex-column flex-md-row align-start align-md-center justify-space-between"
                  >
                    <div class="ml-0 ml-md-8 d-flex flex-wrap align-center">
                      <span class="text-caption text-sm-body-2">{{
                        formatCardNumber(card.cart_number)
                      }}</span>
                      <span
                        v-if="card.owner_name"
                        class="d-none d-sm-inline mx-1"
                        >|</span
                      >
                      <span
                        v-if="card.owner_name"
                        class="d-block d-sm-inline width-100-mobile text-caption text-sm-body-2"
                      >
                        {{ card.owner_name }}
                      </span>
                    </div>

                    <div
                      class="font-weight-bold text-success text-caption text-sm-body-2 mt-1 mt-md-0"
                      v-if="card.max_tx_amount"
                    >
                      {{
                        card.max_tx_amount > 0
                          ? formatCurrency(card.max_tx_amount) + " تومان "
                          : " نامحدود "
                      }}
                    </div>
                  </div>
                </div>

                <div class="d-flex flex-column flex-sm-row mr-2">
                  <v-btn
                    color="primary"
                    icon
                    small
                    class="mb-1 mb-sm-0"
                    @click="openEditMode(card)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    color="error"
                    icon
                    small
                    @click="confirmDelete(card.id)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>

          <div
            class="status-bar mt-3"
            v-if="
              card.approved_count > 0 ||
              card.reserved_count > 0 ||
              card.remaining_count > 0
            "
          >
            <div class="status-item approved" v-if="card.approved_count > 0">
              <span class="status-number">200000000 </span>
              <span class="status-label">تأیید شده</span>
            </div>

            <div class="status-item reserved" v-if="card.reserved_count > 0">
              <span class="status-number">1500000</span>
              <span class="status-label">رزرو شده</span>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <div v-else-if="!pageLoading" class="text-center grey--text my-10 py-10">
      <v-icon color="grey lighten-2" size="64"
        >mdi-credit-card-off-outline</v-icon
      >
      <div class="mt-4">هیچ کارتی ثبت نشده است.</div>
    </div>

    <custom-popup
      id="add-card"
      v-model="showAddCard"
      :cons="cons"
      max-width="800"
      :hideConfirm="true"
      :persistent="true"
    >
      <v-stepper v-model="currentStep" class="elevation-0" non-linear>
        <v-stepper-header class="elevation-0 border-bottom">
          <v-stepper-step :complete="currentStep > 1" editable step="1">
            <span class="d-none d-sm-inline">اطلاعات بانکی</span>
            <span class="d-inline d-sm-none">اطلاعات</span>
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step
            :complete="currentStep > 2"
            :editable="currentStep > 1"
            step="2"
          >
            <span class="d-none d-sm-inline">قوانین مبالغ</span>
            <span class="d-inline d-sm-none">مبالغ</span>
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :editable="currentStep > 2" step="3">
            <span class="d-none d-sm-inline">قوانین انقضا</span>
            <span class="d-inline d-sm-none">انقضا</span>
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1" class="pa-2 pa-md-6">
            <v-form ref="step1Form" lazy-validation @submit.prevent>
              <v-row class="mt-2">
                <v-col cols="12" sm="6">
                  <v-text-field
                    :rules="[rules.required, rules.cardNumber]"
                    :value="form.cart_number"
                    append-icon="mdi-credit-card"
                    dense
                    dir="ltr"
                    label="شماره کارت"
                    maxlength="19"
                    outlined
                    placeholder="۱۶ رقم"
                    @input="(v) => handleCardInput(english(v))"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="form.bank_name"
                    :rules="[rules.required, rules.minLen, rules.nameOnly]"
                    dense
                    label="نام بانک"
                    outlined
                  >
                    <template v-slot:append>
                      <img
                        v-if="bankLogo"
                        :src="bankLogo"
                        alt="logo"
                        style="width: 24px; height: 24px; object-fit: contain"
                      />
                      <v-icon v-else>mdi-bank</v-icon>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    :value="form.shaba_number"
                    @input="handleShebaInput"
                    :rules="[rules.required, rules.sheba]"
                    dense
                    label="شماره شبا (بدون IR)"
                    outlined
                    dir="ltr"
                    placeholder="...01000"
                    maxlength="24"
                  >
                    <template v-slot:append>
                      <div class="mt-1">
                        <p
                          class="m-0 text--black font-weight-bold"
                          style="font-size: 18px"
                        >
                          IR
                        </p>
                      </div>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    :value="form.account_number"
                    @input="
                      (v) => handleNumericInput('account_number', english(v))
                    "
                    :rules="[rules.required, rules.numeric]"
                    append-icon="mdi-notebook"
                    dense
                    label="شماره حساب"
                    outlined
                    dir="ltr"
                    type="text"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="form.owner_name"
                    :rules="[rules.required, rules.nameOnly]"
                    append-icon="mdi-account"
                    dense
                    label="نام صاحب کارت"
                    outlined
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    :value="form.phone"
                    @input="(v) => handleNumericInput('phone', english(v))"
                    :rules="phoneRules"
                    append-icon="mdi-cellphone"
                    dense
                    label="شماره همراه"
                    maxlength="12"
                    outlined
                    dir="ltr"
                    type="text"
                  />
                </v-col>
                <v-col cols="12" v-if="showSmsPlatform">
                  <div class="d-flex align-center flex-wrap">
                    <v-switch
                      v-model="smsSwitch"
                      label="ارسال پیامک"
                      color="primary"
                      inset
                      dense
                      hide-details
                      class="ml-6 mt-0 pt-0"
                    ></v-switch>

                    <v-switch
                      v-model="whatsappSwitch"
                      label="ارسال واتساپ"
                      color="green darken-1"
                      inset
                      dense
                      hide-details
                      class="mt-0 pt-0"
                    ></v-switch>
                  </div>

                  <v-expand-transition>
                    <div v-if="smsDescription" class="mt-3">
                      <v-alert
                        dense
                        text
                        border="left"
                        type="info"
                        class="caption mb-0"
                        icon="mdi-information-outline"
                      >
                        {{ smsDescription }}
                      </v-alert>
                    </div>
                  </v-expand-transition>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.description"
                    append-icon="mdi-text"
                    dense
                    label="توضیحات"
                    outlined
                  />
                </v-col>
              </v-row>

              <div class="d-flex justify-end mt-4">
                <v-btn color="primary" @click="nextStep(1)"
                  >مرحله بعد
                  <v-icon right>mdi-chevron-left</v-icon>
                </v-btn>
              </div>
            </v-form>
          </v-stepper-content>

          <v-stepper-content step="2" class="pa-2 pa-md-6">
            <v-form ref="step2Form" lazy-validation @submit.prevent>
              <p class="caption">
                خالی گذاشتن و یا صفر گذاشتن فیلد های زیر یه منزله بی نهایت است!
              </p>
              <v-row class="mt-2">
                <v-col cols="12">
                  <v-text-field
                    :rules="[rules.numeric]"
                    :value="form.max_tx_amount"
                    append-icon="mdi-cash-multiple"
                    dense
                    dir="ltr"
                    label="سقف مبلغ کل تراکنش ها ( ریال )"
                    outlined
                    placeholder="ریال"
                    type="text"
                    @input="
                      (v) => handleCurrencyInput('max_tx_amount', english(v))
                    "
                  />

                  <div
                    class="grey--text font-weight-bold text-center"
                    style="font-size: 14px; margin-top: -15px"
                    v-if="form.max_tx_amount"
                  >
                    {{ formattedTomans(form.max_tx_amount) }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    :rules="[rules.numeric, validateMaxAmount]"
                    :value="form.max_amount"
                    append-icon="mdi-cash-multiple"
                    dense
                    dir="ltr"
                    label="سقف مبلغ هر واریز ( ریال )"
                    outlined
                    placeholder="ریال"
                    type="text"
                    @input="
                      (v) => handleCurrencyInput('max_amount', english(v))
                    "
                  />
                  <div
                    class="grey--text font-weight-bold text-center"
                    style="font-size: 14px; margin-top: -15px"
                    v-if="form.max_amount"
                  >
                    {{ formattedTomans(form.max_amount) }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    :rules="[rules.numeric, validateMinAmount]"
                    :value="form.min_amount"
                    append-icon="mdi-cash-minus"
                    dense
                    dir="ltr"
                    label="حداقل مبلغ هر واریز ( ریال )"
                    outlined
                    placeholder="ریال"
                    type="text"
                    @input="
                      (v) => handleCurrencyInput('min_amount', english(v))
                    "
                  />
                  <div
                    class="grey--text font-weight-bold text-center"
                    style="font-size: 14px; margin-top: -15px"
                    v-if="form.min_amount"
                  >
                    {{ formattedTomans(form.min_amount) }}
                  </div>
                </v-col>
              </v-row>

              <div class="d-flex justify-space-between mt-4">
                <v-btn color="error" text @click="prevStep(2)">
                  <v-icon left small>mdi-arrow-right</v-icon>
                  بازگشت
                </v-btn>

                <v-btn color="primary" @click="nextStep(2)"
                  >مرحله بعد
                  <v-icon right>mdi-chevron-left</v-icon>
                </v-btn>
              </div>
            </v-form>
          </v-stepper-content>

          <v-stepper-content step="3" class="pa-2 pa-md-6">
            <v-form ref="step3Form" lazy-validation @submit.prevent>
              <v-row class="mt-2">
                <v-col class="pb-0" cols="12" sm="6">
                  <v-text-field
                    :rules="[rules.required, rules.numeric]"
                    :value="form.transaction_expire"
                    append-icon="mdi-av-timer"
                    dense
                    label="زمان انتظار (دقیقه)"
                    outlined
                    type="text"
                    maxlength="2"
                    @input="
                      (v) =>
                        handleNumericInput('transaction_expire', english(v))
                    "
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <custom-date-picker
                    v-model="form.expire_date"
                    :min="nowDateTimeString()"
                    :rules="[rules.required]"
                    append-icon="mdi-calendar"
                    dense
                    label="تاریخ انقضا"
                    outlined
                    type="date"
                  />
                </v-col>

                <v-col cols="12">
                  <v-select
                    v-model="form.limit_mode"
                    :items="limitPeriodOptions"
                    :rules="[rules.required]"
                    dense
                    label="بازه زمانی سقف"
                    placeholder="لطفا انتخاب کنید..."
                    outlined
                  >
                  </v-select>

                  <v-text-field
                    v-if="form.limit_mode && form.limit_mode !== 'unLimited'"
                    :label="`تعداد تراکنش مجاز`"
                    :rules="[rules.numeric, rules.required]"
                    :value="form.max_tx_count"
                    append-icon="mdi-counter"
                    class="mt-3"
                    dense
                    outlined
                    type="text"
                    @input="
                      (v) => handleNumericInput('max_tx_count', english(v))
                    "
                  />
                </v-col>
              </v-row>

              <div class="d-flex justify-space-between mt-4 pb-2">
                <v-btn class="btn-back" @click="prevStep(3)">
                  بازگشت
                  <v-icon small>mdi-arrow-right</v-icon>
                </v-btn>

                <v-btn
                  class="btn-download"
                  :loading="loading"
                  @click="handleSubmit"
                >
                  {{ form.id ? "ویرایش و ذخیره" : "ثبت نهایی کارت" }}
                  <v-icon right>mdi-check</v-icon>
                </v-btn>
              </div>
            </v-form>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </custom-popup>

    <v-btn
      class="py-5"
      color="success"
      outlined
      style="width: 100%"
      @click="openAddMode"
    >
      <v-icon left>mdi-plus</v-icon>
      افزودن کارت جدید
    </v-btn>

    <archived-cards-dialog
      ref="archiveDialog"
      :qr-id="currentQrId"
      :archivedCardsCount="archivedCardsCount"
      @update:archivedCardsCount="archivedCardsCount = $event"
      @restored="onCardRestored"
    />
  </div>
</template>

<style scoped>
.grab-handle {
  cursor: grab;
}

.grab-handle:active {
  cursor: grabbing;
}

.drag-item-wrapper {
  transition: all 0.2s ease;
}

.drag-item-wrapper.dragging {
  opacity: 0.5;
  background-color: #f0f0f0;
  border: 1px dashed #1976d2;
}

.border-bottom {
  border-bottom: 1px solid #eee;
}

.status-bar {
  display: flex;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f5f5;
  border: 1px solid #ddd;
  min-height: 24px;
  flex-wrap: wrap; /* در صورت کمبود فضا، آیتم‌ها بشکنند */
}

.status-item {
  flex: 1;
  text-align: center;
  padding: 2px 4px;
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  min-width: 120px; /* حداقل عرض برای جلوگیری از له شدن محتوا در موبایل */
}

.status-item .status-number {
  font-weight: bold;
  font-size: 13px;
}

.status-item .status-label {
  font-size: 11px;
  opacity: 0.9;
}

/* مدیا کوئری برای سایز موبایل کوچک */
@media (max-width: 600px) {
  .text-subtitle-1-mobile {
    font-size: 1rem !important;
  }

  .width-100-mobile {
    width: 100%;
    display: block;
  }

  .status-item {
    flex-direction: column; /* در موبایل متن و عدد زیر هم باشند */
    gap: 0;
    line-height: 1.2;
  }

  .status-item .status-number {
    font-size: 11px;
  }

  .status-item .status-label {
    font-size: 9px;
  }
}

.status-item.approved {
  background: #4caf50;
  color: white;
}

.status-item.reserved {
  background: #ffeb3b;
  color: black;
}

.status-item.remain {
  background: #e0e0e0;
  color: black;
}
</style>
